import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { ReactComponent as LocationPin } from "assets/icons/location-pin.svg";
import { format } from "date-fns";
import { handlePageClick, handlePageSizeChange } from "helpers/PaginationHelper";
import { useResponsiveWidth } from "hooks/useResponsiveWidth";
import { isFarmer } from "modules/auth";
import {
    Button,
    CustomTable,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
    useAccessToken,
} from "modules/common";
import React, { useMemo, useState } from "react";
import {
    downloadEducationCertificate,
    Education,
    EducationsResponse,
    getEducationStatus,
    getMyAccount,
} from "services";
import { TabLabel } from "views";
import { EducationReminderModal } from "./EducationReminderModal";

interface Props {
    isLoading: boolean;
    educations?: EducationsResponse;
    setSelectedEducation: React.Dispatch<React.SetStateAction<Education | null>>;
    currentTab: TabLabel;
    roleType?: number;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    pageSize: OptionType<string>;
    setPageSize: React.Dispatch<React.SetStateAction<OptionType<string>>>;
}

const PAGE_NEIGHBOURS = 1;

export const EducationsTable: React.FC<Props> = ({
    isLoading,
    educations,
    setSelectedEducation,
    currentTab,
    roleType,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
}) => {
    const accessToken = useAccessToken();

    const [education, setEducation] = useState<Education>();
    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
    const isMobileOrTablet = useResponsiveWidth();

    const formatDateTime = useMemo(
        () => (startDate: Date, endDate: Date) => {
            const formatTime = (data: string) => format(new Date(data), "HH:mm");

            const dayName = startDate.toLocaleDateString("hr-HR", { weekday: "long" });
            const formattedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);

            const formattedDate = startDate
                .toLocaleDateString("hr-HR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                })
                .replace(/\s/g, "");

            const startTime = formatTime(startDate.toISOString());
            const endTime = formatTime(endDate.toISOString());

            return (
                <div>
                    <div>{`${formattedDayName} ${formattedDate}`}</div>
                    <div className="education-time-row">{`${startTime} - ${endTime}`}</div>
                </div>
            );
        },
        []
    );

    const tableColumns = useMemo(() => {
        const columns: TableColumn<Education>[] = [
            {
                Header: "NAZIV",
                disableSortBy: true,
                accessor: "title",
            },
            {
                Header: "ADRESA",
                disableSortBy: true,
                accessor: "address",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    const addressWithCounty = original.county
                        ? `${original.address}, ${original.county}`
                        : original.address;
                    return (
                        <div className="f-r f-ai-c g-12">
                            <LocationPin className="details-icon location-icon" />
                            <span>{addressWithCounty}</span>
                        </div>
                    );
                },
            },
            {
                Header: "DATUM",
                disableSortBy: true,
                accessor: (data: Education) => {
                    const startDate = new Date(data.startDate);
                    const endDate = new Date(data.endDate);
                    return formatDateTime(startDate, endDate);
                },
            },
            {
                Header: "VIŠE",
                disableSortBy: true,
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return (
                        <DetailsIcon
                            className="details-icon"
                            onClick={() => setSelectedEducation(original)}
                        />
                    );
                },
            },
        ];

        if (currentTab === TabLabel.Applied || currentTab === TabLabel.Reminders) {
            columns.push({
                Header: "",
                id: "centered",
                disableSortBy: true,
                Cell: ({ row }: any) => {
                    if (new Date(row.original.registrationDeadline) > new Date()) {
                        return (
                            <div>
                                <Button
                                    size="xxsmall"
                                    variant={
                                        row.original.educationReminders.length !== 0
                                            ? "primary"
                                            : "primary-ghost"
                                    }
                                    onClick={() => {
                                        setIsReminderModalOpen(prev => !prev);
                                        setEducation(row.original);
                                    }}
                                >
                                    Podsjeti me
                                </Button>
                            </div>
                        );
                    } else {
                        return <></>;
                    }
                },
            });
        }

        if (currentTab === TabLabel.Listened && isFarmer(roleType)) {
            columns.push({
                Header: "",
                id: "centered",
                disableSortBy: true,
                Cell: ({ row }: any) => {
                    return (
                        <Button
                            className="educations-table-button"
                            type="button"
                            size="small"
                            variant="primary"
                            onClick={() => onDownloadCertificate(row.original)}
                        >
                            <span>Zatraži potvrdu</span>
                        </Button>
                    );
                },
            });
        }

        return columns;
    }, [currentTab]);

    const onDownloadCertificate = async (data: Education) => {
        const educationId = data.id;
        try {
            const user = await getMyAccount(accessToken!);
            const mibpg = user.data.account.mibpg;
            const res = await getEducationStatus(educationId, 2, mibpg!, accessToken!);

            const payload = res?.data ?? res;
            const uid = payload?.uid;

            if (!uid) {
                console.warn("EducationStatus returned without uid", payload);
                return;
            }

            await downloadEducationCertificate(accessToken!, String(uid));
        } catch (err) {
            console.error("Error fetching EducationStatus or downloading certificate:", err);
        }
    };
    return (
        <div className="education-documents-table">
            <div className="f-c g-24">
                <div className="education-documents-custom-table">
                    <CustomTable
                        data={educations?.educationsList ?? []}
                        columns={tableColumns}
                        loading={isLoading}
                        flex
                        tableType={TableType.EducationsTable}
                    />
                </div>

                {!isLoading && educations && educations.educationsCount !== 0 && (
                    <Pagination
                        className="education-documents-table-pagination"
                        currentPage={currentPage}
                        menuPlacement={isMobileOrTablet ? "top" : "bottom"}
                        numberOfPages={Math.ceil(
                            educations.educationsCount / parseInt(pageSize.value)
                        )}
                        pageNeighbours={PAGE_NEIGHBOURS}
                        pageSize={pageSize}
                        onPageSizeChange={v => handlePageSizeChange(v, setPageSize, setCurrentPage)}
                        handlePageClick={e => handlePageClick(e, setCurrentPage)}
                        handlePageSelect={e => setCurrentPage(e)}
                        next={() => setCurrentPage(p => p + 1)}
                        prev={() => setCurrentPage(p => p - 1)}
                        first={() => setCurrentPage(1)}
                        last={() =>
                            setCurrentPage(
                                Math.ceil(educations.educationsCount / parseInt(pageSize.value))
                            )
                        }
                    />
                )}
            </div>

            {education && (
                <EducationReminderModal
                    isReminderModalOpen={isReminderModalOpen}
                    onClose={() => setIsReminderModalOpen(false)}
                    education={education}
                />
            )}
        </div>
    );
};
